/* The official SoundCloud widget, typed narrowly around the transport controls
 * the persistent HGR player uses. This deliberately does not touch the custom
 * stream API: a public episode URL is handed straight to SoundCloud's iframe
 * and every command crosses the iframe through their supported Widget API. */

export interface SoundCloudProgress {
  currentPosition?: number;
  loadProgress?: number;
  relativePosition?: number;
}

export interface SoundCloudWidget {
  bind: (eventName: string, listener: (event?: SoundCloudProgress) => void) => void;
  unbind: (eventName: string) => void;
  load: (
    url: string,
    options?: {
      auto_play?: boolean;
      buying?: boolean;
      callback?: () => void;
      download?: boolean;
      sharing?: boolean;
      show_artwork?: boolean;
      show_comments?: boolean;
      show_playcount?: boolean;
      show_user?: boolean;
      single_active?: boolean;
    },
  ) => void;
  play: () => void;
  pause: () => void;
  seekTo: (milliseconds: number) => void;
  setVolume: (volume: number) => void;
  getDuration: (callback: (milliseconds: number) => void) => void;
  getPosition: (callback: (milliseconds: number) => void) => void;
  isPaused: (callback: (paused: boolean) => void) => void;
}

export interface SoundCloudWidgetEvents {
  ERROR: string;
  FINISH: string;
  PAUSE: string;
  PLAY: string;
  PLAY_PROGRESS: string;
  READY: string;
  SEEK: string;
}

export interface SoundCloudApi {
  Widget: ((iframe: HTMLIFrameElement) => SoundCloudWidget) & {
    Events: SoundCloudWidgetEvents;
  };
}

declare global {
  interface Window {
    SC?: SoundCloudApi;
  }
}

const SCRIPT_ID = 'hgr-soundcloud-widget-api';
const SCRIPT_URL = 'https://w.soundcloud.com/player/api.js';
let loading: Promise<SoundCloudApi> | null = null;

/** Load SoundCloud's own Widget API once, only after somebody asks to play it. */
export function loadSoundCloudApi(): Promise<SoundCloudApi> {
  if (window.SC?.Widget) return Promise.resolve(window.SC);
  if (loading) return loading;

  loading = new Promise<SoundCloudApi>((resolve, reject) => {
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    const timeout = window.setTimeout(() => {
      script?.remove();
      reject(new Error('The SoundCloud player took too long to load.'));
    }, 15_000);

    const ready = () => {
      window.clearTimeout(timeout);
      if (window.SC?.Widget) resolve(window.SC);
      else reject(new Error('SoundCloud loaded without its Widget API.'));
    };
    const failed = () => {
      window.clearTimeout(timeout);
      script?.remove();
      reject(new Error('The SoundCloud player could not be loaded.'));
    };

    if (script) {
      script.addEventListener('load', ready, { once: true });
      script.addEventListener('error', failed, { once: true });
      return;
    }

    script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.addEventListener('load', ready, { once: true });
    script.addEventListener('error', failed, { once: true });
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    // A transient network failure should be retryable on the next press.
    loading = null;
    throw error;
  });

  return loading;
}

/** The iframe URL for the first SoundCloud episode loaded into the transport. */
export function soundCloudWidgetUrl(trackUrl: string): string {
  const params = new URLSearchParams({
    url: trackUrl,
    // First use is deliberately a load/configure step. A second listener press
    // sends setVolume, seekTo and play synchronously through the ready Widget,
    // avoiding both autoplay rejection and a burst at the wrong volume/time.
    auto_play: 'false',
    buying: 'false',
    download: 'false',
    sharing: 'false',
    show_artwork: 'false',
    show_comments: 'false',
    show_playcount: 'false',
    show_user: 'false',
    single_active: 'true',
  });
  return `https://w.soundcloud.com/player/?${params.toString()}`;
}
